import { de } from './locales/de';
import { en } from './locales/en';
import { es } from './locales/es';
import { fr } from './locales/fr';
import { ja } from './locales/ja';
import { ko } from './locales/ko';
import { ptBR } from './locales/pt-BR';
import { ru } from './locales/ru';
import { zhHans } from './locales/zh-Hans';
import { zhHant } from './locales/zh-Hant';
import type { PluralForms, Translation } from './types';

export type { Translation, PluralForms };

/** Registry. Order here is the order shown in the picker. */
export const LOCALES = {
  en,
  ja,
  ko,
  'zh-Hans': zhHans,
  'zh-Hant': zhHant,
  de,
  fr,
  es,
  'pt-BR': ptBR,
  ru,
} as const satisfies Record<string, Translation>;

export type LocaleCode = keyof typeof LOCALES;
export const LOCALE_CODES = Object.keys(LOCALES) as LocaleCode[];
export const DEFAULT_LOCALE: LocaleCode = 'en';

const STORAGE_KEY = 'uno:locale';

/* ------------------------------------------------------------------ */
/* Detection                                                           */
/* ------------------------------------------------------------------ */

/**
 * Aliases the browser reports that do not match a registry key directly.
 *
 * Chinese is the fiddly one: browsers send region tags (`zh-TW`, `zh-HK`)
 * rather than script tags, and the script is what actually decides which text
 * a reader can use — so region has to be mapped to script by hand.
 */
const ALIASES: Record<string, LocaleCode> = {
  zh: 'zh-Hans',
  'zh-cn': 'zh-Hans',
  'zh-sg': 'zh-Hans',
  'zh-my': 'zh-Hans',
  'zh-hans': 'zh-Hans',
  'zh-tw': 'zh-Hant',
  'zh-hk': 'zh-Hant',
  'zh-mo': 'zh-Hant',
  'zh-hant': 'zh-Hant',
  pt: 'pt-BR',
  'pt-br': 'pt-BR',
  'pt-pt': 'pt-BR', // close enough to be useful; European Portuguese welcome as a PR
};

/**
 * Resolves one BCP 47 tag to a locale we ship, most specific match first:
 * exact tag → alias → base language → base language of any shipped locale.
 * Returns null when nothing is close enough.
 */
export function matchLocale(tag: string): LocaleCode | null {
  const lower = tag.toLowerCase();

  const exact = LOCALE_CODES.find((code) => code.toLowerCase() === lower);
  if (exact) return exact;

  if (ALIASES[lower]) return ALIASES[lower];

  const base = lower.split('-')[0];
  if (ALIASES[base]) return ALIASES[base];

  const byBase = LOCALE_CODES.find((code) => code.toLowerCase().split('-')[0] === base);
  return byBase ?? null;
}

/**
 * Picks the best locale for this visitor: a saved choice wins, otherwise the
 * browser's preference list is walked in order (`navigator.languages` is
 * ordered by the user's own priority), falling back to English.
 */
export function detectLocale(): LocaleCode {
  const saved = readStored();
  if (saved) return saved;

  const preferences = [
    ...(navigator.languages ?? []),
    navigator.language,
  ].filter(Boolean) as string[];

  for (const tag of preferences) {
    const match = matchLocale(tag);
    if (match) return match;
  }
  return DEFAULT_LOCALE;
}

function readStored(): LocaleCode | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw && raw in LOCALES ? (raw as LocaleCode) : null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Store                                                               */
/* ------------------------------------------------------------------ */

let current: LocaleCode = DEFAULT_LOCALE;
const listeners = new Set<() => void>();

/** Called once at startup, before the first render. */
export function initLocale(): void {
  setLocale(detectLocale(), false);
}

export function getLocale(): LocaleCode {
  return current;
}

/** The active catalogue. Safe to call outside React. */
export function t(): Translation {
  return LOCALES[current];
}

export function setLocale(code: LocaleCode, persist = true): void {
  if (!(code in LOCALES)) return;
  current = code;
  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* private mode — the choice just won't survive a reload */
    }
  }
  const meta = LOCALES[code].meta;
  document.documentElement.lang = meta.tag;
  document.documentElement.dir = meta.dir;
  for (const fn of listeners) fn();
}

export function subscribeLocale(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

export type Vars = Record<string, string | number>;

/** Fills `{name}` placeholders. Unknown placeholders are left untouched. */
export function fmt(template: string, vars: Vars = {}): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in vars ? String(vars[key]) : whole,
  );
}

/**
 * Chooses the right plural form for `count` using the active locale's CLDR
 * rules, then fills placeholders. `{count}` is provided automatically.
 */
export function plural(forms: PluralForms, count: number, vars: Vars = {}): string {
  let category: Intl.LDMLPluralRule = 'other';
  try {
    category = new Intl.PluralRules(LOCALES[current].meta.tag).select(count);
  } catch {
    category = count === 1 ? 'one' : 'other';
  }
  const template = forms[category] ?? forms.other;
  return fmt(template, { count, ...vars });
}
